let DeviceUtil = require("../util/DeviceUtil");
let AnnotationUtil = require("../util/AnnotationUtil");
let AnnotationForm = require("./AnnotationForm");
let ResizableChart = require("./ResizableChart");

let Legend = mc.charts.Legend;

class RecordingChart extends ResizableChart {
  constructor(props) {
    super(props);

    this.state = { ...this.state,
      annotation: null,
      last: "",
      bound: 0
    };
  }

  componentDidMount() {
    super.componentDidMount();

    this.renderChart(this.props, this.state);
  }

  shouldComponentUpdate(nextProps, nextState) {
    this.renderChart(nextProps, nextState);

    return true;
  }

  getExtent(values, channel, autoScale) {
    let extent = d3.extent(values.v || [].concat(values.x, values.y, values.z));
    let config = channel.recording.sensorConfig;
    let m;
    let n;

    if (!autoScale) {
      switch (channel.type) {
        case "ACCEL":
          n = config.gyro ? config.gyro.accelRange : config.accel.range;
          extent = [-n, n];
          break;
        case "GYRO":
          n = config.gyro.gyroRange;
          extent = [-n, n];
          break;
        case "ECG":
        case "EMG":
          m = d3.median(values.v);
          n = Math.abs(m) * 0.08;
          extent = [Math.max(extent[0], m - n), Math.min(extent[1], m + n)];
      }
    }

    return extent;
  }

  normalizeValues(channel) {
    if (channel.type.match(/ECG|EMG/)) {
      return { v: channel.values.v.map((v) => v * 1000) };
    }

    return channel.values;
  }

  getLabels(dimensions) {
    return dimensions.map(function (d) {
      return {
        "x": "X",
        "y": "Y",
        "z": "Z",
        "v": "mV"
      }[d];
    });
  }

  getColors(dimensions) {
    let { BLUE, RED, GREEN, ORANGE } = mc.ui.colors;

    return dimensions.map(function (d) {
      return {
        "x": BLUE,
        "y": RED,
        "z": GREEN,
        "v": ORANGE
      }[d];
    });
  }

  getXScale(width, startTs, period, factor) {
    let domain = [startTs, startTs + period];
    let range = [0, width * factor];

    return d3.scaleUtc().domain(domain).range(range);
  }

  getYScale(domain, factor) {
    let height = 175;
    let range = [height * factor, 0];

    return d3.scaleLinear().domain(domain).range(range);
  }

  drawLine(canvas, xScale, yScale, timestamps, values, color) {
    let ctx = canvas.getContext("2d");
    let i = 0;
    let len = values.length;

    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(xScale(timestamps[i]), yScale(values[i]));

    for (i = 1; i < len; i++) {
      ctx.lineTo(xScale(timestamps[i]), yScale(values[i]));
    }

    ctx.stroke();
  }

  renderChart(props, state) {
    let {
      studyId,
      subjectId,
      authorId,
      channel,
      annotations,
      timezone,
      period,
      startTs,
      autoScale,
      disabled
    } = props;

    let {
      width,
      annotation,
      bound,
      last
    } = state;

    let height = 175;
    let colors = this.getColors(channel.dimensions);
    let values = this.normalizeValues(channel);
    let canvasContainer = d3.select(this.refs.canvasContainer);
    let svg = d3.select(this.refs.chart);
    let annoNode = svg.select(".rc-rec-chart-annotations");
    let surface = svg.select(".rc-rec-chart-surface");
    let needle = svg.select(".rc-rec-chart-needle");
    let timeStamp = svg.select(".rc-rec-chart-ts");
    let startMarker = svg.select(".rc-rec-chart-start-marker");
    let stopMarker = svg.select(".rc-rec-chart-stop-marker");
    let xAxisNode = svg.select(".rc-rec-chart-x-axis");
    let yAxisNode = svg.select(".rc-rec-chart-y-axis");
    let [yMin, yMax] = this.getExtent(values, channel, autoScale);
    let ySteps = channel.type === "ACCEL" ? [0.1, 0.2, 0.5, 1, 2, 4, 8, 16] : [0.1, 0.2, 0.5];
    let yTickValues = mc.math.ticks(yMin, yMax, ySteps, 10);
    let xScale = this.getXScale(width, startTs, period, 1);
    let yScale = this.getYScale(d3.extent(yTickValues), 1);

    let x1 = annotation ? xScale(annotation.startTs) : 0;
    let x2 = annotation ? xScale(annotation.stopTs) : 0;

    let xTicks = 1;

    if (period > 6000) {
      xTicks = 5;
    }

    if (period > 30000) {
      xTicks = 10;
    }

    let xAxis = d3.axisBottom()
      .scale(xScale)
      .tickFormat(function (d) {
        return mc.format.time(d, timezone, "long");
      })
      .ticks(d3.timeSecond, xTicks);

    let yAxis = d3.axisLeft()
      .scale(yScale)
      .tickValues(yTickValues);

    xAxisNode
      .transition()
      .call(xAxis);

    yAxisNode
      .transition()
      .call(yAxis);

    let rects = annoNode.selectAll("rect").data(annotations);

    rects = rects
      .enter()
      .append("rect")
      .merge(rects);

    rects
      .attr("x", function (d) {
        return xScale(d.startTs) - (d.startTs === d.stopTs ? 5 : 0);
      })
      .attr("y", function (d, i) {
        return d.event.type === "recording" ? 5 : 20;
      })
      .attr("width", function (d, i) {
        return Math.max(10, xScale(d.stopTs) - xScale(d.startTs));
      })
      .attr("height", 10)
      .attr("rx", 5)
      .attr("ry", 5)
      .attr("fill", function (d, i) {
        let { PINK, HOT_BLUE } = mc.ui.colors;

        if (d.event.type === "recording") {
          if (state.annotation && state.annotation.id !== d.id) {
            return "#ccc";
          }

          return PINK;
        }

        return HOT_BLUE;
      })
      .attr("data-type", function (d) {
        return d.event.type;
      })
      .attr("data-tooltip", function (d) {
        let desc = AnnotationUtil.getDescription(d);

        if (d.stopTs !== d.startTs) {
          desc += " (" + mc.format.duration(d.stopTs - d.startTs) + ")";
        }

        return desc;
      });

    rects.on("mouseup", (function (d, i) {
      if (disabled) {
        return;
      }

      if (d.event.type === "recording") {
        let annotation = {
          id: d.id,
          startTs: d.startTs,
          stopTs: d.stopTs,
          value: d.value,
          studyId: d.studyId,
          subjectId: d.subjectId,
          authorId: d.authorId,
          eventId: d.event.id
        };

        this.setState({ annotation, bound: 0 });
      }
    }).bind(this));

    rects.exit().remove();

    canvasContainer
      .selectAll("canvas")
      .remove();

    canvasContainer
      .append("canvas")
      .attr("class", "rc-rec-chart-canvas")
      .attr("width", (width - 65) * 2)
      .attr("height", (height * 2) + "px")
      .style("width", (width - 65) + "px")
      .style("height", height);

    let canvas = canvasContainer.select("canvas").node();

    channel.dimensions.forEach(function (d, i) {
      let xScale = this.getXScale(width, startTs, period, 2);
      let yScale = this.getYScale(d3.extent(yTickValues), 2);

      this.drawLine(canvas, xScale, yScale, channel.times, values[d], colors[i]);
    }, this);

    surface.on("mousemove", function () {
      let point = d3.mouse(surface.node());
      let x = point[0];

      needle.attr("transform", "translate(" + x + ")");
      timeStamp.text(mc.format.time(xScale.invert(x), timezone, "precise"));
    });

    surface.on("mouseup", (function () {
      if (disabled) {
        return;
      }

      let point = d3.mouse(surface.node());
      let x = point[0];
      let markTs = xScale.invert(x).getTime();

      if (annotation === null) {
        bound = 0;
        annotation = {
          startTs: markTs,
          stopTs: markTs,
          value: last,
          studyId,
          subjectId,
          authorId,
          eventId: channel.recording.id
        };
      }
      else if (bound === 0) {
        if (annotation.stopTs === annotation.startTs) {
          annotation.stopTs = markTs;
        }
        annotation.startTs = markTs;
      }
      else if (bound === 1) {
        annotation.stopTs = markTs;
      }

      if (annotation.startTs > annotation.stopTs) {
        bound = +!bound;
        annotation._startTs = annotation.startTs;
        annotation.startTs = annotation.stopTs;
        annotation.stopTs = annotation._startTs;
      }

      this.setState({
        annotation,
        bound
      });
    }).bind(this));

    startMarker.attr("transform", "translate(" + Math.min(x1, x2) + ")");
    stopMarker.attr("transform", "translate(" + Math.max(x1, x2) + ")");
  }

  setBound(bound) {
    this.setState({ bound });
  }

  editAnnotation(annotation) {
    this.setState({ annotation });
  }

  saveAnnotation(annotation, method) {
    this.props.saveAnnotation(annotation, method);

    this.setState({
      annotation: null,
      last: annotation.value
    });
  }

  cancelEditing() {
    this.setState({
      annotation: null
    });
  }

  formatSampleRate(sc, channelType) {
    let args;

    switch (channelType) {
      case "ACCEL":
        args = /MPU/.test(sc.sensingType) ? ["periodMs", sc.gyro.periodMs] : ["rate", sc.accel.rate];
        break;
      case "EMG":
      case "ECG":
        args = ["rate", sc.afe.rate];
        break;
      case "GYRO":
        args = ["periodMs", sc.gyro.periodMs];
        break;
    }

    return DeviceUtil.formatValue.apply(null, args);
  }

  render() {
    let {
      channel,
      timezone,
      deviceSerial,
      disabled
    } = this.props;

    let {
      annotation,
      bound,
      width
    } = this.state;

    let pc = channel.recording.physicalConfig || { location: "???", side: "???" };
    let sc = channel.recording.sensorConfig;
    let labels = this.getLabels(channel.dimensions);
    let colors = this.getColors(channel.dimensions);
    let height = 175;

    let legendItems = labels.map(function (label, i) {
      return { label, color: colors[i], bullet: Legend.CIRCLE };
    });

    let grid = [0.25, 0.50, 0.75].map(function (d, i) {
      return <line key={i} x1="0" y1={height * d} x2={width - 65} y2={height * d}/>;
    });

    return (
      <div className="rc-rec-chart" data-disabled={disabled}>
        <div className="rc-rec-chart-head">
          <Legend items={legendItems}/>
          <h3>
            {tx(pc.location) + (pc.side.match(/RIGHT|LEFT/) ? (" – " + tx("side." + pc.side)) : "")}
          </h3>
          <span className="rc-study-device-configs-list-modality" data-modality={channel.type}>
            {channel.type.replace(/ECG|EMG/, "ELECTRODES")}
          </span>
          <small>
            {this.formatSampleRate(sc, channel.type)}
            {" (" + (deviceSerial || "???") + ")"}
           </small>
        </div>
        <div className="rc-rec-chart-body">
          <div ref="canvasContainer"/>
          <svg ref="chart" className="rc-rec-chart-svg" width={width} height="240">
            <defs>
              <clipPath id="rc-rec-chart-clip">
                <rect x="0" y="0" width={width - 65} height={height}/>
              </clipPath>
            </defs>
            <g className="rc-rec-chart-surface" transform="translate(65,5)">
              <rect x="0" y="0" width={width - 65} height={height} fill="white"/>
              <g className="rc-rec-chart-grid">
                {grid}
              </g>
              <line x1="0" y1="0" x2="0" y2="175" className="rc-rec-chart-start-marker"/>
              <line x1="0" y1="0" x2="0" y2="175" className="rc-rec-chart-stop-marker"/>
              <g className="rc-rec-chart-needle">
                <line x1="0" y1="0" x2="0" y2="175"/>
                <text transform="translate(5,12)" className="rc-rec-chart-ts"></text>
              </g>
            </g>
            <text className="rc-rec-chart-y-label" x="-10" y="10" textAnchor="middle" transform="rotate(-90) translate(-87.5,0)" width={height}>
              {tx("unit." + channel.type)}
            </text>
            <g className="rc-rec-chart-y-axis" transform="translate(65,5)"/>
            <g className="rc-rec-chart-x-axis" transform="translate(65,215)" clipPath="url(#rc-rec-chart-clip)"/>
            <g transform="translate(65,180)">
              <rect fill="#eee" width={width - 65} height={35}/>
              <g className="rc-rec-chart-annotations" clipPath="url(#rc-rec-chart-clip)"/>
            </g>
          </svg>
        </div>
        { annotation ? (
          <div className="rc-rec-chart-form">
            <AnnotationForm
              annotation={annotation}
              bound={bound}
              timezone={timezone}
              onEditBound={this.setBound.bind(this)}
              onEdit={this.editAnnotation.bind(this)}
              onSave={this.saveAnnotation.bind(this)}
              onCancel={this.cancelEditing.bind(this)}/>
          </div>
        ) : null}
      </div>
    );
  }
}

module.exports = RecordingChart;